import { Component, EventEmitter, Inject, LOCALE_ID, OnInit, Output } from '@angular/core';
import { ApplicationFacade, LoginFacade, NotificationFacade, RecoverPasswordFacade } from '@ridango/components';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent, ResetCompleteEvent } from '@ridango/core';
import { takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { passwordValidator } from '@ridango/core/lib/validators/password.validator';

@Component({
    selector: 'gl-reset-password-v2',
    templateUrl: './password-recover-verification-page.component.html',
    styleUrls: ['./password-recover-verification-page.component.scss']
})
export class PasswordRecoverVerificationPageV2Component extends BaseComponent implements OnInit {
    @Output() resetComplete = new EventEmitter<ResetCompleteEvent>();

    public assetsPath$ = this.applicationFacade.assetsPath$;
    public showPassword = false;
    public showPasswordRepeat = false;
    public passwordForm: FormGroup;
    public token: string;

    constructor(
        @Inject(LOCALE_ID) public locale: string,
        @Inject('ENV') public env: Env,
        private recoverPassword: RecoverPasswordFacade,
        private applicationFacade: ApplicationFacade,
        private notification: NotificationFacade,
        private translate: TranslateService,
        private loginFacade: LoginFacade,
        private route: ActivatedRoute,
        private router: Router
    ) {
        super();
    }

    ngOnInit() {
        this.passwordForm = new FormGroup({
            password: new FormControl('', passwordValidator),
            passwordRepeat: new FormControl('', passwordValidator)
        });

        this.token = this.route.snapshot.params.token;
        this.initializeListener();
    }

    public handleBackArrow(): void {
        this.router.navigate([this.translate.currentLang, 'login']);
    }

    public submitPassword() {
        if (this.passwordForm.valid) {
            if (this.passwordForm.controls['password'].value === this.passwordForm.controls['passwordRepeat'].value) {
                const password = this.passwordForm.value.password;
                this.recoverPassword.setNewPassword(password, this.token, this.locale, false);
            } else {
                this.notification.error(this.translate.instant('LOCAL_PASSWORDS_MUST_MATCH'));
            }
        } else {
            this.passwordForm.controls.password.markAsTouched();
            this.passwordForm.controls.passwordRepeat.markAsTouched();
        }
    }

    private handleSuccess() {
        this.loginFacade.isAuthenticated$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                (isAuthenticated) =>
                    isAuthenticated && this.resetComplete.emit({ loginSuccessful: true, authAccessToken: null })
            );

        this.loginFacade.error$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((error) => error && this.resetComplete.emit({ loginSuccessful: false, authAccessToken: null }));
    }

    private initializeListener() {
        // listen to state for error
        this.recoverPassword.error$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                (error) => error && this.notification.error(this.translate.instant('LOCAL_CHANGE_PASSWORD_FAILED'))
            );

        // listen to state for password change done
        this.recoverPassword.done$.pipe(takeUntil(this.componentDestroyed$)).subscribe((done) => {
            if (!done) {
                return;
            }

            const loginWhenResetPassword = this.env.REGISTRATION?.LOGIN_WHEN_RESET_PASSWORD ?? true;

            if (loginWhenResetPassword) {
                return this.handleSuccess();
            }

            return this.resetComplete.emit({ loginSuccessful: true, authAccessToken: null });
        });
    }
    getFirstError(control: AbstractControl, errorCodes: string[]): string | undefined {
        return errorCodes.find((errorCode) => control.hasError(errorCode));
    }
}
